import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

export function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const dismissed = sessionStorage.getItem('homeuni_pwa_dismissed');
    if (dismissed) return;

    if (ios) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('homeuni_pwa_dismissed', 'true');
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] animate-slide-up" data-testid="pwa-install-prompt">
      <div className="mx-3 mb-3 bg-white border border-[#1565C0]/25 rounded-xl shadow-lg shadow-[#1565C0]/15 p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-[#EFF6FF] border border-[#BBDEFB] flex items-center justify-center flex-shrink-0">
          <img src="/main-logo-horinzontal.png" alt="HomeUni" className="w-9 h-9 object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#1565C0] text-sm font-semibold">Install HomeUni</p>
          {isIOS ? (
            <p className="text-slate-500 text-xs mt-0.5">
              Tap <Share className="w-3 h-3 inline text-[#1976D2]" /> then <strong>"Add to Home Screen"</strong>
            </p>
          ) : (
            <p className="text-slate-500 text-xs mt-0.5">Get quick access from your home screen</p>
          )}
        </div>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            className="bg-[#1565C0] text-white text-xs font-semibold px-4 py-2 rounded-md hover:bg-[#0D47A1] transition-colors flex items-center gap-1.5 flex-shrink-0"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-[#1565C0] transition-colors p-1 flex-shrink-0"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
